'use client';

import { Card, CardContent, EmptyState, SectionHeading, Spinner } from '@lectura/ui';
import { Library } from 'lucide-react';
import Link from 'next/link';
import { LoginRequired } from '@/components/LoginRequired';
import { SiteShell } from '@/components/SiteShell';
import { useAuth } from '@/lib/auth';
import { useCollections } from '@/lib/queries';
import { useHydrated } from '@/lib/use-hydrated';

/**
 * Kollekciók listaoldala — minden tenant-szintű kollekció böngészése.
 *
 * Auth-köteles: a `catalog-svc` `/collections` végpontja `TenantScope`-os, így
 * bejelentkezés nélkül 401-et kapnánk. Guest-renderben a `LoginRequired`
 * üres-state jelenik meg a 401-zaj helyett.
 */
export default function CollectionsPage() {
  const hydrated = useHydrated();
  const isLogged = useAuth((s) => s.isAuthenticated());
  const { data, isLoading, isError } = useCollections();
  const items = data ?? [];

  if (!hydrated || !isLogged) {
    return (
      <LoginRequired
        title="A kollekciók csak bejelentkezve böngészhetők"
        description="Lépj be, hogy a tenanted tematikus csoportjait lásd."
      />
    );
  }

  return (
    <SiteShell>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Tematikus"
          title="Kollekciók"
          description="Sorozatok, témakörök és válogatások — a kiadó által rendezett csoportokba szerveztük a katalógust."
        />

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spinner className="size-8" />
          </div>
        ) : isError ? (
          <EmptyState
            icon={<Library />}
            title="Nem érhetők el a kollekciók"
            description="A katalógus szolgáltatás átmenetileg nem válaszol. Próbáld újra később."
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Library />}
            title="Még nincs kollekció"
            description="Az admin felületen létrehozott kollekciók itt jelennek meg."
          />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((c) => (
              <Link
                key={c.id}
                href={`/collections/${c.id}` as const}
                aria-label={`Kollekció: ${c.name}`}
              >
                <Card className="h-full transition-shadow hover:shadow-card-hover">
                  <CardContent className="flex h-full flex-col gap-2 p-6">
                    <div className="flex items-center gap-2">
                      <span className="flex size-10 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                        <Library className="size-5" />
                      </span>
                      <h3 className="font-display text-lg font-semibold leading-tight">
                        {c.name}
                      </h3>
                    </div>
                    {c.description ? (
                      <p className="line-clamp-3 text-sm text-[var(--color-foreground-muted)]">
                        {c.description}
                      </p>
                    ) : null}
                    <p className="mt-auto text-xs font-medium text-brand-600">
                      {c.itemCount} kiadvány
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </SiteShell>
  );
}
